'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import React, { forwardRef, useImperativeHandle } from 'react'
import { useForm } from 'react-hook-form'
import { type z } from 'zod'

import { Form } from '@/legacy-src/components/ui/form'
import { cn } from '@/legacy-src/lib/utils'

import { FormFieldRenderer } from './FormFieldRenderer'
import { type FieldProcessor, type SectionType } from './types'

interface ConfigurableFormProps<T extends z.ZodSchema<any> = z.ZodSchema<any>> {
  sections: SectionType[]
  title?: string
  description?: string
  onSubmit: (values: z.infer<T>) => Promise<void>
  fieldProcessor?: FieldProcessor
  formSchema: T
  id?: string
  defaultValues?: Record<string, unknown>
}

export interface ConfigurableFormRef {
  validateAndProceed: () => Promise<boolean>
  submitForm: () => Promise<boolean>
}

export const ConfigurableForm = forwardRef<ConfigurableFormRef, ConfigurableFormProps>(
  ({ sections, title, description, onSubmit, fieldProcessor, formSchema, id, defaultValues }, ref) => {
    const form = useForm<z.infer<typeof formSchema>>({
      resolver: zodResolver(formSchema),
      defaultValues,
    })

    // Process sections with current form data for dynamic field modification
    const processedSections = fieldProcessor ? fieldProcessor(sections, form.watch()) : sections

    const validateAndProceed = async (): Promise<boolean> => {
      return await form.trigger()
    }

    const submitForm = async (): Promise<boolean> => {
      const isValid = await form.trigger()
      if (!isValid) {
        return false
      }

      try {
        const values = form.getValues()
        await onSubmit(values)
        return true
      } catch (error) {
        console.error('Form submission error:', error)
        return false
      }
    }

    const handleFormSubmit = form.handleSubmit(async (values) => {
      await onSubmit(values)
    })

    useImperativeHandle(ref, () => ({
      validateAndProceed,
      submitForm,
    }))

    return (
      <Form {...form}>
        {title && <h1 className="mb-4 text-3xl font-bold">{title}</h1>}
        {description && <p className="mb-6">{description}</p>}

        <form onSubmit={handleFormSubmit} className="space-y-6" id={id}>
          <div>
            {processedSections.map((section, index) => (
              <div key={section.id || index} className="mb-6 last:mb-0">
                {section.title && <h2 className="mb-4 text-2xl font-bold">{section.title}</h2>}

                <div className="grid grid-cols-1 gap-6 tablet:grid-cols-2">
                  {section.fields.map((field) => {
                    return (
                      <div key={field.id} className={cn('col-span-1 tablet:col-span-2', field.fieldClassName)}>
                        <FormFieldRenderer field={field} control={form.control} autoFocus={field.autoFocus} />
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Hidden submit button to enable form submission via Enter key */}
          <button type="submit" hidden />
        </form>
      </Form>
    )
  },
)

ConfigurableForm.displayName = 'ConfigurableForm'
